import { HttpErrorResponse } from '@angular/common/http';
import {
	ChangeDetectionStrategy,
	Component,
	inject,
	signal,
} from '@angular/core';
import {
	AbstractControl,
	FormControl,
	FormGroup,
	ReactiveFormsModule,
} from '@angular/forms';
import { AlertService } from '@context/frontend/alert';
import { DialogBase, DialogOptions } from '@context/frontend/common';
import { AlertId } from '@context/shared/types/alert';
import { Organization } from '@context/shared/types/organization';
import { TranslocoModule } from '@jsverse/transloco';
import { TuiButton, TuiError, TuiInput } from '@taiga-ui/core';
import { TuiButtonLoading, TuiTextarea } from '@taiga-ui/kit';
import { AdminOrganizationService } from '../../services';

@Component({
	standalone: true,
	imports: [
		TranslocoModule,
		ReactiveFormsModule,
		TuiButton,
		TuiButtonLoading,
		TuiInput,
		TuiError,
		TuiTextarea,
	],
	selector: 'ctx-create-organization-dialog',
	templateUrl: 'create-organization-dialog.component.html',
	styleUrl: 'create-organization-dialog.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateOrganizationDialogComponent extends DialogBase<Partial<Organization> | null> {
	static override readonly DialogOptions = {
		label: 'admin.organizations.create',
		size: 's',
	} as DialogOptions;

	private readonly organizationService = inject(AdminOrganizationService);
	private readonly alert = inject(AlertService);

	form: FormGroup | null = null;
	readonly busy = signal(false);

	constructor() {
		super();
		this.setupForm();
	}

	getRequiredValidator(field: AbstractControl, errorKey: string) {
		if (!field.value && field.touched) {
			return { [errorKey]: true };
		}
		return null;
	}

	getEmailValidator(field: AbstractControl, errorKey: string) {
		if (field.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value)) {
			return { [errorKey]: true };
		}
		return null;
	}

	setupForm() {
		this.form = new FormGroup({
			name: new FormControl(null, (field) =>
				this.getRequiredValidator(field, 'form.error.name.required'),
			),
			ownerEmail: new FormControl(null, [
				(field) =>
					this.getEmailValidator(field, 'form.error.email.valid'),
				(field) =>
					this.getRequiredValidator(
						field,
						'form.error.email.required',
					),
			]),
			ownerGivenName: new FormControl(null),
			ownerFamilyName: new FormControl(null),
			ownerMessage: new FormControl(null),
		});
	}

	async onSubmit() {
		this.form?.markAllAsTouched();
		if (!this.form || this.form.invalid || this.busy()) return;

		this.busy.set(true);
		this.form.disable();

		try {
			const value = this.form.getRawValue();

			// Call the API to create organization and invite owner
			const result =
				await this.organizationService.createOrganizationWithOwner({
					name: value.name,
					ownerEmail: value.ownerEmail,
					ownerGivenName: value.ownerGivenName || undefined,
					ownerFamilyName: value.ownerFamilyName || undefined,
					ownerMessage: value.ownerMessage || undefined,
				});

			this.alert.open('organization-updated').subscribe();
			this.onDismiss(result);
		} catch (error) {
			console.error('Error creating organization:', error);
			this.alert.open(handleError(error)).subscribe();
		} finally {
			this.busy.set(false);
			this.form?.enable();
		}
	}

	onCancel() {
		this.onDismiss(null);
	}
}

function handleError(error: unknown): AlertId {
	switch ((error as HttpErrorResponse)?.error?.message) {
		case 'invite/cannot-invite-super':
			return 'organization-invite-super-error';
		default:
			return 'organization-updated-error';
	}
}
